/**
 * Accounting Services › Invoicing & Receivables: open invoices (create, email from QuickBooks,
 * record payments), the collections list of past-due customers, and receivables by age.
 */
import { formatCents } from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ExternalLink, Plus, Send, Trash2 } from "lucide-react";
import { useState } from "react";
import { MoneyInput, type ListRow } from "@/components/bookkeeping";
import { askText } from "@/components/dialogs";
import { Empty, Loading } from "@/components/page";
import { AgingTable, PaymentDialog, SubTabs } from "@/components/payables";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { api, errorMessage } from "@/lib/api";
import { formatDate, formatDateTime, plural } from "@/lib/format";
import { hasPermission, type Me } from "@/lib/session";
import { useToast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";

type Invoice = { id: string; customer: string; customerId: string | null; docNumber: string; date: string; dueDate: string; total: number; balance: number; daysOverdue: number; email: string | null; lastSent: string | null; link: string };
type Data = {
  today: string;
  open: Invoice[];
  aging: { columns: string[]; rows: Array<{ name: string; amounts: Record<string, number> }> };
  collections: Array<{ customer: string; customerId: string | null; pastDue: number; invoices: number; oldestDays: number; lastSent: string | null }>;
  depositTo: Array<{ qboId: string; name: string; type: string }>;
};
type Line = { itemId: string; description: string; qty: string; rateCents: number; classId: string | null };

const field = "h-9 rounded-md border border-input bg-background px-2 text-sm";
/** Quantity × rate, rounded to the cent (the server works it out the same way). */
const amountOf = (l: Line) => {
  const q = Number(l.qty);
  return Number.isFinite(q) && q > 0 ? Math.round((Math.round(q * 10_000) * l.rateCents) / 10_000) : 0;
};

export function InvoicingReceivables({ clientId, me }: { clientId: string; me: Me }) {
  const { toast } = useToast();
  const canEdit = hasPermission(me, "engagements.write");
  const [tab, setTab] = useState<"open" | "collections" | "aging">("open");
  const { data, isLoading, error } = useQuery<Data>({ queryKey: [`/api/clients/${clientId}/books/receivables`], retry: false });
  const [creating, setCreating] = useState(false);
  const [paying, setPaying] = useState<Invoice | null>(null);
  const remind = useMutation({
    meta: { inlineError: true },
    mutationFn: (v: { id: string; email?: string }) => api.send<{ sentTo: string }>("POST", `/api/clients/${clientId}/books/invoices/${v.id}/send`, v.email ? { email: v.email } : {}),
    onSuccess: (r) => toast({ title: "Invoice emailed from QuickBooks", description: `Sent to ${r.sentTo}.` }),
  });
  const send = async (inv: Invoice) => {
    const email = inv.email ?? (await askText({ title: `Email invoice ${inv.docNumber ? `#${inv.docNumber}` : ""}`, description: "QuickBooks emails the invoice with its payment link.", label: "Customer's email", required: true, confirmLabel: "Send", validate: (v) => (/^\S+@\S+\.\S+$/.test(v) ? null : "Enter an email address") }));
    if (email) remind.mutate({ id: inv.id, ...(inv.email ? {} : { email }) });
  };
  if (isLoading) return <Loading />;
  if (error || !data) return <Empty>{errorMessage(error)}</Empty>;
  const pastDue = data.open.filter((i) => i.daysOverdue > 0).reduce((a, i) => a + i.balance, 0);
  return (
    <div>
      <SubTabs
        value={tab}
        onChange={setTab}
        tabs={[
          ["open", "Open invoices", data.open.length],
          ["collections", "Collections", data.collections.length],
          ["aging", "Receivables aging"],
        ]}
      />
      {remind.error && <p className="mb-3 text-sm text-destructive">{errorMessage(remind.error)}</p>}
      {tab === "open" && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-muted-foreground">
              {formatCents(data.open.reduce((a, i) => a + i.balance, 0))} open across {data.open.length} invoice{data.open.length === 1 ? "" : "s"}
              {pastDue > 0 && <span className="text-amber-700 dark:text-amber-300"> · {formatCents(pastDue)} past due</span>}
            </p>
            {canEdit && (
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus className="h-3.5 w-3.5" /> New invoice
              </Button>
            )}
          </div>
          {!data.open.length ? (
            <Empty>No open invoices in QuickBooks.</Empty>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {data.open.map((inv) => (
                <li key={inv.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 basis-full sm:basis-0">
                    <span className="block truncate font-medium">{inv.customer}</span>
                    <span className="block text-xs text-muted-foreground">
                      {inv.docNumber && `#${inv.docNumber} · `}
                      {formatDate(inv.date)} · due {formatDate(inv.dueDate)}
                      {inv.lastSent && ` · emailed ${formatDateTime(inv.lastSent)}`}
                    </span>
                  </span>
                  {inv.daysOverdue > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-900 dark:bg-red-900/30 dark:text-red-200">{plural(inv.daysOverdue, "day")} past due</span>}
                  <span className="tabular text-right">
                    <span className="block font-medium">{formatCents(inv.balance)}</span>
                    {inv.balance !== inv.total && <span className="block text-xs text-muted-foreground">of {formatCents(inv.total)}</span>}
                  </span>
                  <a href={inv.link} target="_blank" rel="noreferrer" className="text-primary" aria-label="Open in QuickBooks">
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  {canEdit && (
                    <span className="flex gap-1">
                      <Button size="sm" variant="ghost" disabled={remind.isPending} onClick={() => send(inv)}>
                        <Send className="h-3.5 w-3.5" /> Email
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setPaying(inv)}>
                        Record payment
                      </Button>
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {tab === "collections" &&
        (!data.collections.length ? (
          <Empty>No customer is past due.</Empty>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {data.collections.map((c) => {
              const oldest = data.open.filter((i) => (i.customerId ?? i.customer) === (c.customerId ?? c.customer) && i.daysOverdue > 0).sort((a, b) => b.daysOverdue - a.daysOverdue)[0];
              return (
                <li key={c.customerId ?? c.customer} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 py-2.5 text-sm">
                  <span className="min-w-0 flex-1 basis-full sm:basis-0">
                    <span className="block truncate font-medium">{c.customer}</span>
                    <span className="block text-xs text-muted-foreground">
                      {c.invoices} invoice{c.invoices === 1 ? "" : "s"} past due · oldest {c.oldestDays} days · {c.lastSent ? `last emailed ${formatDateTime(c.lastSent)}` : "not emailed from ClientFlow"}
                    </span>
                  </span>
                  <span className={cn("tabular font-medium", c.oldestDays > 60 ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-300")}>{formatCents(c.pastDue)}</span>
                  {canEdit && oldest && (
                    <Button size="sm" variant="outline" disabled={remind.isPending} onClick={() => send(oldest)}>
                      <Send className="h-3.5 w-3.5" /> Send reminder
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        ))}
      {tab === "aging" && <AgingTable aging={data.aging} who="Customer" />}
      {creating && <InvoiceDialog clientId={clientId} today={data.today} onClose={() => setCreating(false)} />}
      {paying && <PaymentDialog kind="invoice" clientId={clientId} item={paying} today={data.today} accounts={data.depositTo} onClose={() => setPaying(null)} />}
    </div>
  );
}

function InvoiceDialog({ clientId, today, onClose }: { clientId: string; today: string; onClose: () => void }) {
  const { toast } = useToast();
  const { data: customers } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/customers`], staleTime: 5 * 60_000, retry: false });
  const { data: items } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/items`], staleTime: 5 * 60_000, retry: false });
  const { data: classes } = useQuery<ListRow[]>({ queryKey: [`/api/clients/${clientId}/books/lists/classes`], staleTime: 5 * 60_000, retry: false });
  const addDays = (d: string, n: number) => new Date(Date.parse(`${d}T12:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);
  const [customerId, setCustomerId] = useState("");
  const [date, setDate] = useState(today);
  const [terms, setTerms] = useState(30);
  const [docNumber, setDocNumber] = useState("");
  const [email, setEmail] = useState("");
  const [memo, setMemo] = useState("");
  const [sendIt, setSendIt] = useState(true);
  const [lines, setLines] = useState<Line[]>([{ itemId: "", description: "", qty: "1", rateCents: 0, classId: null }]);
  const activeItems = (items ?? []).filter((i) => i.active);
  const activeClasses = (classes ?? []).filter((c) => c.active);
  const customer = (customers ?? []).find((c) => c.id === customerId);
  const total = lines.reduce((a, l) => a + amountOf(l), 0);
  const set = (i: number, patch: Partial<Line>) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));
  // One key for this form: sending it again (a double click, a retry) never makes a second invoice.
  const [key] = useState(() => crypto.randomUUID());
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: () =>
      api.send<{ docNumber: string | null; sendError: string | null }>("POST", `/api/clients/${clientId}/books/invoices`, {
        customerId,
        date,
        dueDate: addDays(date, terms),
        ...(docNumber ? { docNumber } : {}),
        memo,
        ...(email ? { email } : {}),
        send: sendIt && !!email,
        lines: lines.map((l) => ({ itemId: l.itemId, description: l.description, qty: Number(l.qty), rateCents: l.rateCents, classId: l.classId })),
      }, key),
    onSuccess: (r) => {
      toast({ title: `Invoice ${r.docNumber ?? ""} created in QuickBooks`, description: r.sendError ? `It wasn't emailed: ${r.sendError}` : sendIt && email ? `Emailed to ${email}.` : undefined, variant: r.sendError ? "destructive" : undefined });
      onClose();
    },
  });
  const ok = !!customerId && total > 0 && lines.every((l) => l.itemId && Number(l.qty) > 0) && (!sendIt || !email || /^\S+@\S+\.\S+$/.test(email));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>New invoice</DialogTitle>
          <DialogDescription>Created in the client's QuickBooks, which numbers it and can email it with its payment link.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-xs text-muted-foreground sm:col-span-2">
            <span className="mb-1 block">Customer</span>
            <select
              className={cn(field, "w-full")}
              value={customerId}
              onChange={(e) => {
                setCustomerId(e.target.value);
                const c = (customers ?? []).find((x) => x.id === e.target.value);
                if (c?.detail.email && !email) setEmail(String(c.detail.email));
              }}
            >
              <option value="">Choose a customer</option>
              {(customers ?? [])
                .filter((c) => c.active)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Invoice number (blank for the next one)</span>
            <Input value={docNumber} maxLength={21} onChange={(e) => setDocNumber(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Invoice date</span>
            <input type="date" className={cn(field, "w-full")} value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Terms</span>
            <select className={cn(field, "w-full")} value={terms} onChange={(e) => setTerms(Number(e.target.value))}>
              {[0, 15, 30, 45, 60].map((n) => (
                <option key={n} value={n}>
                  {n ? `Net ${n} (due ${formatDate(addDays(date, n))})` : "Due on receipt"}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-muted-foreground">
            <span className="mb-1 block">Customer's email</span>
            <Input type="email" value={email} maxLength={100} onChange={(e) => setEmail(e.target.value)} placeholder={customer ? "billing@customer.com" : ""} />
          </label>
          <label className="text-xs text-muted-foreground sm:col-span-3">
            <span className="mb-1 block">Message on the invoice</span>
            <Input value={memo} maxLength={1000} onChange={(e) => setMemo(e.target.value)} placeholder="Thank you for your business." />
          </label>
        </div>
        <div className="space-y-2">
          <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,2fr)_5rem_7.5rem_7rem_2rem] gap-2 px-1 text-xs font-medium text-muted-foreground sm:grid">
            <span>Product or service</span>
            <span>Description</span>
            <span className="text-right">Qty</span>
            <span className="text-right">Rate</span>
            <span className="text-right">Amount</span>
            <span />
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-2 gap-2 rounded-md border border-border p-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_5rem_7.5rem_7rem_2rem] sm:border-0 sm:p-0">
              <select
                aria-label={`Product or service, line ${i + 1}`}
                className={cn(field, "col-span-2 w-full min-w-0 sm:col-span-1")}
                value={l.itemId}
                onChange={(e) => {
                  const it = activeItems.find((x) => x.id === e.target.value);
                  set(i, { itemId: e.target.value, ...(it?.detail.price ? { rateCents: Math.round(Number(it.detail.price) * 100) } : {}), ...(it?.detail.description && !l.description ? { description: String(it.detail.description) } : {}) });
                }}
              >
                <option value="">Choose</option>
                {activeItems.map((it) => (
                  <option key={it.id} value={it.id}>
                    {it.name}
                  </option>
                ))}
              </select>
              <div className="col-span-2 flex gap-2 sm:col-span-1">
                <Input aria-label={`Description, line ${i + 1}`} value={l.description} onChange={(e) => set(i, { description: e.target.value })} />
                {activeClasses.length > 0 && (
                  <select aria-label={`Class, line ${i + 1}`} className={cn(field, "w-24 shrink-0")} value={l.classId ?? ""} onChange={(e) => set(i, { classId: e.target.value || null })}>
                    <option value="">No class</option>
                    {activeClasses.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <Input aria-label={`Quantity, line ${i + 1}`} inputMode="decimal" className="h-9 text-right tabular" value={l.qty} onChange={(e) => set(i, { qty: e.target.value.replace(/[^0-9.]/g, "") })} />
              <MoneyInput label={`Rate, line ${i + 1}`} value={l.rateCents} onChange={(c) => set(i, { rateCents: c })} />
              <span className="tabular flex items-center justify-end text-sm">{formatCents(amountOf(l))}</span>
              <button type="button" aria-label={`Remove line ${i + 1}`} disabled={lines.length <= 1} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="flex items-center justify-center text-muted-foreground hover:text-destructive disabled:opacity-30">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2 text-sm">
            <Button size="sm" variant="ghost" onClick={() => setLines((ls) => [...ls, { itemId: "", description: "", qty: "1", rateCents: 0, classId: null }])} disabled={lines.length >= 100}>
              <Plus className="h-3.5 w-3.5" /> Add line
            </Button>
            <span className="tabular font-semibold">Total {formatCents(total)}</span>
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={sendIt} onChange={(e) => setSendIt(e.target.checked)} /> Email the invoice from QuickBooks{email ? ` to ${email}` : " (enter the customer's email)"}
        </label>
        {save.error && <p className="text-sm text-destructive">{errorMessage(save.error)}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!ok || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? "Creating…" : sendIt && email ? "Create and email" : "Create invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
